/**
 * Token Counter
 *
 * Uses the JavaScript lexer to count meaningful tokens in code.
 * Excludes from the count:
 *   - Whitespace and comments
 *   - Periods (property access)
 *   - Commas (separators)
 *   - Semicolons (terminators)
 *   - Closing brackets: ), ], }
 *   - Unary minus signs (negative number prefix, not subtraction)
 */

import type { CodeFile } from '@fc0/types'

export const MAX_TOKENS = 9999
import { createJSLexer, jsDescriptor } from './jsLexer'
import type { Token } from './lexer'

/** Find the previous non-whitespace/comment token. */
function getPrevMeaningful(tokens: Token[], index: number): Token | null {
  for (let i = index - 1; i >= 0; i--) {
    const type = jsDescriptor.typeToString(tokens[i].type)
    if (
      type !== 'WHITESPACE' &&
      type !== 'LINE_COMMENT' &&
      type !== 'BLOCK_COMMENT'
    ) {
      return tokens[i]
    }
  }
  return null
}

/** Find the next non-whitespace/comment token. */
function getNextMeaningful(tokens: Token[], index: number): Token | null {
  for (let i = index + 1; i < tokens.length; i++) {
    const type = jsDescriptor.typeToString(tokens[i].type)
    if (
      type !== 'WHITESPACE' &&
      type !== 'LINE_COMMENT' &&
      type !== 'BLOCK_COMMENT'
    ) {
      return tokens[i]
    }
  }
  return null
}

const VALUE_KEYWORDS = new Set(['this', 'true', 'false', 'null', 'undefined'])

/**
 * Check whether a minus sign in the given context is binary subtraction.
 * If the previous meaningful token is a value-producing expression
 * (identifier, number, string, closing bracket, postfix operator,
 * or a value-like keyword), the minus is subtraction.
 * Otherwise it's a unary negative sign.
 */
function isBinaryMinusContext(prev: Token | null): boolean {
  if (!prev) return false
  const type = jsDescriptor.typeToString(prev.type)
  if (
    type === 'IDENTIFIER' ||
    type === 'NUMBER' ||
    type === 'STRING' ||
    type === 'TEMPLATE'
  )
    return true
  if (prev.text === ')' || prev.text === ']' || prev.text === '}') return true
  if (prev.text === '++' || prev.text === '--') return true
  if (type === 'KEYWORD' && VALUE_KEYWORDS.has(prev.text)) return true
  return false
}

/**
 * Count tokens in a JavaScript code string.
 * Returns the number of meaningful tokens after filtering.
 */
export function countTokens(code: string): number {
  if (!code) return 0

  const lexer = createJSLexer(code)
  const tokens: Token[] = []
  let token
  while ((token = lexer.getToken()) !== null) {
    tokens.push(token)
  }

  let count = 0
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]
    const typeName = jsDescriptor.typeToString(t.type)

    // Skip whitespace and comments
    if (
      typeName === 'WHITESPACE' ||
      typeName === 'LINE_COMMENT' ||
      typeName === 'BLOCK_COMMENT'
    )
      continue

    // Skip periods (property access)
    if (t.text === '.') continue

    // Skip commas
    if (t.text === ',') continue

    // Skip semicolons
    if (t.text === ';') continue

    // Skip closing brackets (only the opening one counts)
    if (t.text === ')' || t.text === ']' || t.text === '}') continue

    // Skip unary minus (negative number sign, not subtraction)
    if (t.text === '-') {
      const next = getNextMeaningful(tokens, i)
      if (next && jsDescriptor.typeToString(next.type) === 'NUMBER') {
        const prev = getPrevMeaningful(tokens, i)
        if (!isBinaryMinusContext(prev)) continue
      }
    }

    count++
  }

  return count
}

/**
 * Count tokens for all code files.
 * Returns per-file counts and a total across all files.
 */
export function countTokensInFiles(files: CodeFile[]): {
  perFile: number[]
  total: number
} {
  const perFile = files.map((f) => countTokens(f.content))
  const total = perFile.reduce((sum, n) => sum + n, 0)
  return { perFile, total }
}
